import React, { useEffect, useMemo, useState } from "react";
import { SupervisorLayout } from "@/layouts/SupervisorLayout";
import client, { API, resolveApiUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MonitorSmartphone, UserRound, Radio, Pause, CircleDot, Clock, ExternalLink,
  Loader2, Cpu, ClipboardList, Plus, Pencil, Cloud, Power, AlertTriangle, CheckCircle2, XCircle, HelpCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

const ORDER_STATUS_STYLE = {
  LIVE:      { bg: "rgba(255,51,51,0.10)",   border: "rgba(255,51,51,0.35)",   fg: "#ff6b6b", icon: Radio,     label: "LIVE" },
  PAUSED:    { bg: "rgba(255,184,68,0.10)",  border: "rgba(255,184,68,0.35)",  fg: "#ffb844", icon: Pause,     label: "PAUSED" },
  IDLE:      { bg: "rgba(255,255,255,0.03)", border: "rgba(255,255,255,0.10)", fg: "rgba(255,255,255,0.55)", icon: CircleDot, label: "AVAILABLE" },
  INACTIVE:  { bg: "rgba(255,255,255,0.02)", border: "rgba(255,255,255,0.10)", fg: "rgba(255,255,255,0.35)", icon: Power,     label: "INACTIVE" },
};
const CF_STATUS_STYLE = {
  CONNECTED:      { fg: "#00e39a", icon: CheckCircle2, label: "CONNECTED" },
  OFFLINE:        { fg: "#ffb844", icon: Pause,        label: "OFFLINE" },
  NOT_CONFIGURED: { fg: "rgba(255,255,255,0.45)", icon: HelpCircle, label: "NOT CONFIGURED" },
  ERROR:          { fg: "#ff6b6b", icon: XCircle,      label: "ERROR" },
  UNKNOWN:        { fg: "rgba(255,255,255,0.55)", icon: HelpCircle, label: "UNKNOWN" },
};

function OrderBadge({ station, activeOrder }) {
  let key = "IDLE";
  if (station.active === false) key = "INACTIVE";
  else if (activeOrder?.status === "LIVE") key = "LIVE";
  else if (activeOrder?.status === "PAUSED") key = "PAUSED";
  const s = ORDER_STATUS_STYLE[key];
  const I = s.icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] tracking-[0.18em] font-medium uppercase"
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.fg }}
      data-testid={`station-state-${station.id}`}>
      <I size={11} /> {s.label}
    </span>
  );
}

function CfPill({ status }) {
  const s = CF_STATUS_STYLE[status] || CF_STATUS_STYLE.UNKNOWN;
  const I = s.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.16em] font-medium uppercase" style={{ color: s.fg }}>
      <I size={11}/> {s.label}
    </span>
  );
}

function StationCard({ station, activeOrder, waitingCount, technicianPhotoUrl, onEdit, onCloudflare, onDeactivate }) {
  const techName = station.technician_name || activeOrder?.technician_name || "";
  const initials = (techName || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join("");
  const cfStatus = station.cloudflare?.status || "NOT_CONFIGURED";
  const busy = activeOrder?.status === "LIVE" || activeOrder?.status === "PAUSED";
  return (
    <div className="rounded-xl border p-5 flex flex-col gap-4" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10", opacity: station.active === false ? 0.55 : 1 }} data-testid={`station-card-${station.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(0,229,255,0.10)", color: "var(--accent)" }}><MonitorSmartphone size={18}/></div>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-white truncate">{station.name || station.id}</div>
            <div className="text-[11px] tracking-[0.2em] font-medium text-white/45 uppercase mt-0.5">{station.id}</div>
          </div>
        </div>
        <OrderBadge station={station} activeOrder={activeOrder}/>
      </div>

      <div className="flex items-center gap-3 rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden shrink-0" style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--accent)" }}>
          {technicianPhotoUrl
            ? <img src={technicianPhotoUrl} alt={techName} className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.style.display='none';}} />
            : initials ? <span className="text-sm font-semibold">{initials}</span> : <UserRound size={16}/>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.2em] font-medium text-white/45 uppercase">Technician</div>
          <div className="text-sm font-medium text-white truncate">{techName || <span className="text-white/40 font-normal">Unassigned</span>}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="text-[10px] tracking-[0.2em] font-medium text-white/45 uppercase flex items-center gap-1.5"><ClipboardList size={10}/> Current</div>
          {activeOrder ? <div className="mt-1 text-sm font-semibold text-white truncate">#{activeOrder.order_number}</div> : <div className="mt-1 text-sm text-white/40 font-normal">No active build</div>}
        </div>
        <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="text-[10px] tracking-[0.2em] font-medium text-white/45 uppercase flex items-center gap-1.5"><Clock size={10}/> Queue</div>
          <div className="mt-1 text-sm font-semibold text-white tabular-nums">{waitingCount}</div>
        </div>
      </div>

      <div className="rounded-lg p-3 flex items-center justify-between" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="text-[10px] tracking-[0.2em] font-medium text-white/45 uppercase flex items-center gap-1.5"><Cloud size={10}/> Cloudflare</div>
        <CfPill status={cfStatus}/>
      </div>

      <div className="mt-auto pt-3 flex items-center justify-end gap-2 border-t flex-wrap" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
        <Button variant="ghost" size="sm" className="text-white/60 hover:text-white h-8 px-3 font-medium" onClick={() => onEdit(station)} data-testid={`station-edit-${station.id}`}><Pencil size={13} className="mr-1.5"/> Edit</Button>
        <Button variant="ghost" size="sm" className="text-white/60 hover:text-white h-8 px-3 font-medium" onClick={() => onCloudflare(station)} data-testid={`station-cf-${station.id}`}><Cloud size={13} className="mr-1.5"/> Cloudflare</Button>
        {activeOrder?.customer_link_path && (
          <Link to={activeOrder.customer_link_path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-medium border transition-colors hover:bg-white/5" style={{ borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.75)" }} data-testid={`station-view-live-${station.id}`}>View Live <ExternalLink size={11}/></Link>
        )}
        <Button variant="ghost" size="sm" className="text-white/40 hover:text-[#ff6b6b] h-8 px-3 font-medium" disabled={busy} onClick={() => onDeactivate(station)} data-testid={`station-deactivate-${station.id}`} title={busy ? "Active build in progress" : "Deactivate station"}><Power size={13}/></Button>
      </div>
    </div>
  );
}

function StationFormModal({ open, onOpenChange, editing, technicians, onSaved }) {
  const isEdit = !!editing;
  const [stationId, setStationId] = useState("");
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [techUid, setTechUid] = useState("");
  const [stationPin, setStationPin] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setStationId(editing.id); setName(editing.name || ""); setActive(editing.active !== false);
      const cur = technicians.find(t => t.station_id === editing.id);
      setTechUid(cur?.id || "");
      setStationPin("");
    } else { setStationId(""); setName(""); setActive(true); setTechUid(""); setStationPin(""); }
  }, [open, editing, technicians]);
  const submit = async (e) => {
    e.preventDefault();
    const trimmed = (name || "").trim(); if (!trimmed) return toast.error("Name is required");
    const pinTrim = (stationPin || "").trim();
    if (pinTrim && !/^\d{6}$/.test(pinTrim)) return toast.error("Station PIN must be exactly 6 digits.");
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const body = { name: trimmed, active, technician_id: techUid || null };
        if (pinTrim) body.station_pin = pinTrim;
        const { data } = await client.patch(`/stations/${editing.id}`, body);
        saved = data;
      } else {
        const sid = stationId.trim().toUpperCase();
        if (!/^ST\d{2,3}$/.test(sid)) return toast.error("Station ID must match ST01, ST12, ST100 (^ST\\d{2,3}$)");
        const body = { station_id: sid, name: trimmed, active, technician_id: techUid || null };
        if (pinTrim) body.station_pin = pinTrim;
        const { data } = await client.post("/stations", body);
        saved = data;
      }
      toast.success(isEdit ? "Station updated" : "Station created");
      onSaved(saved); onOpenChange(false);
    } catch (err) {
      const raw = err.response?.data?.detail;
      const map = { station_id_already_exists: "That station id already exists.", invalid_station_id_format: "Station id must match ST01, ST12, ST100.", invalid_station_pin_format: "Station PIN must be exactly 6 digits.", station_has_active_build: "Blocked — this station has an active LIVE/PAUSED build.", target_station_already_assigned: "That technician is already assigned to another station.", current_station_active_build: "That technician's current station has an active build." };
      toast.error(map[raw] || formatApiError(raw) || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-white/10" style={{ background: "#0a0a0d", color: "#fff" }} data-testid="station-form-modal">
        <DialogHeader><DialogTitle className="text-xl font-semibold tracking-tight">{isEdit ? `Edit ${editing.id}` : "Add Station"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-5">
          <div>
            <Label className="text-[11px] tracking-[0.2em] text-white/55 uppercase font-medium">Station ID</Label>
            <Input value={stationId} onChange={(e) => setStationId(e.target.value)} placeholder="e.g. ST02" disabled={isEdit} className="mt-2 h-10 bg-black border-white/15 text-white uppercase tracking-widest font-mono" data-testid="station-id-input"/>
            <div className="text-[11px] text-white/40 mt-1 font-normal">Must match <code className="text-white/60">^ST\d{"{2,3}"}$</code>. Immutable after creation.</div>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.2em] text-white/55 uppercase font-medium">Station Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Assembly Station 2" className="mt-2 h-10 bg-black border-white/15 text-white" data-testid="station-name-input"/>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.2em] text-white/55 uppercase font-medium">Assigned Technician</Label>
            <select value={techUid} onChange={(e) => setTechUid(e.target.value)} className="mt-2 w-full h-10 rounded-md bg-black border border-white/15 text-white px-3 text-sm font-normal focus:outline-none" data-testid="station-tech-select">
              <option value="">Unassigned</option>
              {technicians.filter(t => t.active !== false).map(t => (
                <option key={t.id} value={t.id} disabled={t.station_id && t.station_id !== editing?.id}>{t.tech_id} — {t.name}{t.station_id && t.station_id !== editing?.id ? ` (on ${t.station_id})` : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.2em] text-white/55 uppercase font-medium">Station PIN</Label>
            <Input
              value={stationPin}
              onChange={(e) => setStationPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder={isEdit ? (editing?.pin_configured ? "•••••• (leave blank to keep)" : "6-digit numeric PIN") : "6-digit numeric PIN"}
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={6}
              className="mt-2 h-10 bg-black border-white/15 text-white tracking-[0.6em] font-mono"
              data-testid="station-pin-input"
            />
            <div className="text-[11px] mt-1 font-normal flex items-center gap-1.5">
              {isEdit && (
                <span
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide"
                  style={editing?.pin_configured
                    ? { background: "rgba(0,255,102,0.08)", color: "#00ff66", border: "1px solid rgba(0,255,102,0.35)" }
                    : { background: "rgba(255,152,0,0.08)", color: "#ffb066", border: "1px solid rgba(255,152,0,0.35)" }}
                  data-testid={`station-pin-status-${editing?.id}`}
                >
                  {editing?.pin_configured ? "PIN CONFIGURED" : "PIN NOT SET"}
                </span>
              )}
              <span className="text-white/40">Unlocks this station only. Stored as a secure hash — never shown again.</span>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg px-4 py-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div><div className="text-sm font-medium">Active</div><div className="text-[11px] text-white/45 font-normal">Inactive stations cannot log in or accept new orders.</div></div>
            <button type="button" onClick={() => setActive(v => !v)} className="relative w-11 h-6 rounded-full" style={{ background: active ? "var(--accent)" : "rgba(255,255,255,0.15)" }} data-testid="station-active-toggle"><span className="absolute top-0.5 h-5 w-5 rounded-full bg-black" style={{ left: active ? "22px" : "2px" }}/></button>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} className="text-white/70 hover:text-white font-medium">Cancel</Button>
            <Button type="submit" disabled={saving} className="h-10 rounded-sm font-medium" style={{ background: "var(--accent)", color: "#000" }} data-testid="station-form-save">{saving ? <><Loader2 className="animate-spin mr-1.5" size={14}/> Saving…</> : isEdit ? "Save changes" : "Create station"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CloudflareModal({ station, onOpenChange, onSaved }) {
  const [uid, setUid] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!station) return;
    setUid(station.cloudflare?.live_input_id || "");
    setStatus(station.cloudflare?.last_status || null);
  }, [station]);
  if (!station) return null;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch(`/stations/${station.id}/cloudflare`, { live_input_uid: uid.trim() });
      setStatus(data.cloudflare?.last_status || null);
      toast.success(`Cloudflare updated — ${data.cloudflare?.status}`);
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true);
    try {
      const { data } = await client.post(`/stations/${station.id}/cloudflare/test`);
      setStatus(data);
      toast[data.connected ? "success" : "message"](`Cloudflare: ${data.status}`);
      onSaved({ ...station, cloudflare: { ...station.cloudflare, status: data.status, last_status: data, last_tested_at: data.tested_at } });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Test failed"); }
    finally { setTesting(false); }
  };
  return (
    <Dialog open={!!station} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-white/10" style={{ background: "#0a0a0d", color: "#fff" }} data-testid="station-cf-modal">
        <DialogHeader><DialogTitle className="text-xl font-semibold tracking-tight flex items-center gap-2"><Cloud size={18}/> Cloudflare — {station.id}</DialogTitle></DialogHeader>
        <div className="mt-2 space-y-5">
          <div className="rounded-lg p-4 flex items-center justify-between" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div><div className="text-[11px] tracking-[0.2em] text-white/45 uppercase font-medium">Status</div><div className="mt-1"><CfPill status={station.cloudflare?.status || "UNKNOWN"}/></div></div>
            <Button type="button" onClick={test} disabled={testing || saving} className="h-9 rounded-sm font-medium border" style={{ background: "rgba(0,229,255,0.10)", borderColor: "rgba(0,229,255,0.35)", color: "var(--accent)" }} data-testid="station-cf-test">{testing ? <><Loader2 className="animate-spin mr-1.5" size={13}/> Testing</> : "Test Connection"}</Button>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.2em] text-white/55 uppercase font-medium">Live Input UID</Label>
            <Input value={uid} onChange={(e) => setUid(e.target.value)} placeholder="Cloudflare Live Input UID (from CF Dashboard → Stream → Live Inputs)" className="mt-2 h-10 bg-black border-white/15 text-white font-mono text-xs" data-testid="station-cf-uid-input"/>
            <div className="text-[11px] text-white/40 mt-1.5 font-normal">Only the Live Input UID is stored. IDEA never accepts, stores or manages RTMP Stream Keys — configure OBS with the key directly from Cloudflare's dashboard.</div>
          </div>
          {status && (
            <div className="rounded-md p-3 text-[12px] font-normal" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="station-cf-last-status">
              <div className="text-white/55">Last tested: {status.tested_at || "—"}</div>
              <div className="text-white/55">Live input reachable: {String(status.live_input_ok)}</div>
              {status.live_input_state && <div className="text-white/55">CF state: {status.live_input_state}</div>}
              {status.reason && <div className="text-[#ffb0b0]">Reason: {status.reason}</div>}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(null)} disabled={saving} className="text-white/70 hover:text-white font-medium">Close</Button>
            <Button type="button" onClick={save} disabled={saving} className="h-10 rounded-sm font-medium" style={{ background: "var(--accent)", color: "#000" }} data-testid="station-cf-save">{saving ? <><Loader2 className="animate-spin mr-1.5" size={14}/> Saving…</> : "Save & Validate"}</Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeactivateConfirm({ station, onClose, onConfirm, saving }) {
  if (!station) return null;
  return (
    <Dialog open={!!station} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md border" style={{ background: "#0a0a0d", color: "#fff", borderColor: "rgba(255,51,51,0.35)" }} data-testid="station-deactivate-modal">
        <DialogHeader><DialogTitle className="text-xl font-semibold flex items-center gap-2"><AlertTriangle size={18} style={{ color: "#ff3333" }}/> Deactivate {station.id}?</DialogTitle></DialogHeader>
        <p className="text-white/70 text-sm font-normal">Deactivating a station hides it from technician login and new-order pickers. Historical builds remain intact. You can reactivate later.</p>
        <DialogFooter className="gap-2 mt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-white/70 hover:text-white font-medium">Cancel</Button>
          <Button type="button" onClick={onConfirm} disabled={saving} className="h-10 rounded-sm font-medium" style={{ background: "#ff3333", color: "#fff" }} data-testid="station-deactivate-confirm">{saving ? <Loader2 className="animate-spin mr-1.5" size={14}/> : <Power size={14} className="mr-1.5"/>} Deactivate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function StationsPage() {
  const [stations, setStations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cfTarget, setCfTarget] = useState(null);
  const [deactTarget, setDeactTarget] = useState(null);
  const [deactSaving, setDeactSaving] = useState(false);

  const load = async () => {
    try {
      const [s, o, t] = await Promise.all([client.get("/stations"), client.get("/orders"), client.get("/technicians")]);
      setStations(s.data || []); setOrders(o.data || []); setTechs(t.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, []);

  const derived = useMemo(() => {
    const active = new Map(), waiting = new Map(), techByStation = new Map();
    orders.forEach(o => {
      if (o.status === "LIVE" || o.status === "PAUSED") active.set(o.station_id, o);
      else if (o.status === "WAITING") waiting.set(o.station_id, (waiting.get(o.station_id) || 0) + 1);
    });
    techs.forEach(t => { if (t.station_id) techByStation.set(t.station_id, t); });
    return { active, waiting, techByStation };
  }, [orders, techs]);

  const doDeactivate = async () => {
    if (!deactTarget) return;
    setDeactSaving(true);
    try {
      await client.delete(`/stations/${deactTarget.id}`);
      toast.success(`${deactTarget.id} deactivated`);
      setDeactTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Deactivate failed"); }
    finally { setDeactSaving(false); }
  };

  return (
    <SupervisorLayout>
      <div className="p-5 md:p-8 max-w-[1600px]">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.28em] font-semibold text-white/45 uppercase flex items-center gap-2"><Cpu size={12}/> Operations</div>
            <h1 className="mt-2 text-2xl md:text-3xl font-semibold text-white tracking-tight" data-testid="stations-heading">Stations</h1>
            <p className="mt-1.5 text-white/50 text-sm max-w-xl font-normal">Manage assembly workstations, technician assignments and streaming configuration.</p>
          </div>
          <Button onClick={() => setAddOpen(true)} data-testid="add-station-button" className="h-10 rounded-sm font-medium" style={{ background: "var(--accent)", color: "#000" }}><Plus size={15} className="mr-1"/> Add Station</Button>
        </div>

        {loading ? (
          <div className="rounded-xl border py-16 text-center text-white/50 flex items-center justify-center gap-2" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0a0a0d" }}><Loader2 className="animate-spin" size={16}/> Loading stations…</div>
        ) : stations.length === 0 ? (
          <div className="rounded-xl border py-16 text-center flex flex-col items-center gap-3" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0a0a0d" }}><MonitorSmartphone size={22} className="text-white/40"/><div className="text-white font-semibold text-base">No stations configured</div><Button onClick={() => setAddOpen(true)} className="mt-2 h-9 rounded-sm font-medium" style={{ background: "var(--accent)", color: "#000" }}><Plus size={14} className="mr-1"/> Add Station</Button></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="stations-grid">
            {stations.map(s => {
              const active = derived.active.get(s.id) || null;
              const waitingCount = derived.waiting.get(s.id) || 0;
              const tech = derived.techByStation.get(s.id);
              const photoUrl = resolveApiUrl(tech?.profile_photo_url);
              return <StationCard key={s.id} station={s} activeOrder={active} waitingCount={waitingCount} technicianPhotoUrl={photoUrl} onEdit={setEditing} onCloudflare={setCfTarget} onDeactivate={setDeactTarget}/>;
            })}
          </div>
        )}
      </div>

      <StationFormModal open={addOpen} onOpenChange={setAddOpen} editing={null} technicians={techs} onSaved={() => load()}/>
      <StationFormModal open={!!editing} onOpenChange={(o) => !o && setEditing(null)} editing={editing} technicians={techs} onSaved={() => load()}/>
      <CloudflareModal station={cfTarget} onOpenChange={setCfTarget} onSaved={() => load()}/>
      <DeactivateConfirm station={deactTarget} onClose={() => setDeactTarget(null)} onConfirm={doDeactivate} saving={deactSaving}/>
    </SupervisorLayout>
  );
}
